import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { submitVendorInvoiceBatch } from '../../utils/agentsApi';
import {
  formatIngestionDate,
  formatJobStatusLabel,
  JOB_COLUMN_WIDTHS,
  JOB_STATUS,
  VENDOR_INVOICE_QUERY_KEYS,
  VENDOR_INVOICE_ROUTES,
} from './vendorInvoice/utils';
import { useVendorInvoiceUploads } from './vendorInvoice/useVendorInvoiceUploads';
import { useVendorInvoiceJobLists } from './vendorInvoice/useVendorInvoiceJobLists';
import VendorInvoiceUploadPanel from './vendorInvoice/VendorInvoiceUploadPanel';
import VendorInvoiceJobTable from './vendorInvoice/VendorInvoiceJobTable';

const pageStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem',
  paddingBottom: '2rem',
};

const emptyStyle = {
  color: 'var(--darkGrey)',
  fontSize: '0.9rem',
};

const summaryRowStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.75rem',
};

const summaryBoxStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.2rem',
  minWidth: '8.5rem',
  padding: '0.65rem 0.85rem',
  border: '1px solid var(--lightGrey)',
  borderRadius: '0.35rem',
  background: 'var(--white)',
  cursor: 'pointer',
  textAlign: 'left',
  font: 'inherit',
  color: 'inherit',
};

const paginationStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '0.5rem',
};

const paginationBtnStyle = {
  border: '1px solid var(--lightGrey)',
  borderRadius: '0.25rem',
  background: 'var(--white)',
  padding: '0.2rem 0.55rem',
  cursor: 'pointer',
  fontSize: '0.875rem',
  lineHeight: 1.2,
};

const statusBadgeStyle = (status) => {
  const base = {
    display: 'inline-block',
    padding: '0.15rem 0.5rem',
    borderRadius: '0.25rem',
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'capitalize',
    background: 'var(--lightestGrey)',
    color: 'var(--darkGrey)',
  };
  if (status === 'finished' || status === 'complete') {
    return { ...base, background: 'var(--lightGreen)', color: 'var(--green)' };
  }
  if (status === 'failed' || status === 'rejected') {
    return { ...base, background: 'var(--lightRed)', color: 'var(--red)' };
  }
  if (
    status?.startsWith('pending') ||
    status === 'processing' ||
    status === 'uncertain' ||
    status === 'incomplete'
  ) {
    return { ...base, background: '#fef9e7', color: 'var(--darkGrey)' };
  }
  return base;
};

const truncateError = (message, maxLen = 120) => {
  const text = String(message ?? '').trim();
  if (!text) {
    return 'Unknown error';
  }
  return text.length > maxLen ? `${text.slice(0, maxLen)}…` : text;
};

const processingStatusLabel = (status) => {
  if (status === JOB_STATUS.PENDING) {
    return 'Queued';
  }
  if (status === JOB_STATUS.PROCESSING) {
    return 'Running';
  }
  return formatJobStatusLabel(status);
};

const widthStyle = (key, extra = {}) => ({
  width: JOB_COLUMN_WIDTHS[key],
  minWidth: JOB_COLUMN_WIDTHS[key],
  maxWidth: JOB_COLUMN_WIDTHS[key],
  ...extra,
});

const VendorInvoicesList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sectionRefs = useRef({});
  const {
    pendingFiles,
    addFiles,
    removeFile,
    clearFiles,
    readyFiles,
    uploading,
  } = useVendorInvoiceUploads();

  const { pendingVendor, pendingReview, failed, processing, archived, error } =
    useVendorInvoiceJobLists();

  const [collapsed, setCollapsed] = useState(() => ({
    pendingVendor: false,
    pendingReview: false,
    failed: false,
    processing: false,
    archived: true,
  }));

  const invalidateLists = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: VENDOR_INVOICE_QUERY_KEYS.allJobs,
    });
  }, [queryClient]);

  const batchMutation = useMutation({
    mutationFn: (s3Keys) => submitVendorInvoiceBatch(s3Keys),
    onSuccess: () => {
      toast.success('Batch upload received. Processing started.');
      clearFiles();
      invalidateLists();
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to submit batch.');
    },
  });

  const canSubmit =
    readyFiles.length > 0 && !uploading && !batchMutation.isPending;

  const handleSubmitBatch = () => {
    const s3Keys = readyFiles.map((f) => f.key).filter(Boolean);
    if (!s3Keys.length) {
      return;
    }
    batchMutation.mutate(s3Keys);
  };

  const navigateToJob = (jobId) => {
    navigate(VENDOR_INVOICE_ROUTES.jobDetail(jobId));
  };

  const toggleSection = (key) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const focusSection = (key) => {
    setCollapsed((prev) => ({ ...prev, [key]: false }));
    requestAnimationFrame(() => {
      sectionRefs.current[key]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  };

  const commonColumns = [
    {
      key: 'vendorName',
      header: 'Vendor',
      style: widthStyle('vendorName'),
      render: (job) => job.vendorName || 'Unknown',
    },
    {
      key: 'createdAt',
      header: 'Ingested',
      style: widthStyle('createdAt'),
      render: (job) => formatIngestionDate(job.createdAt),
    },
    {
      key: 'updatedBy',
      header: 'Last touched',
      style: widthStyle('updatedBy'),
      render: (job) => job.updatedBy || '-',
    },
  ];

  const vendorColumns = [
    commonColumns[0],
    {
      key: 'fileCount',
      header: 'Files',
      style: widthStyle('fileCount'),
      render: (job) => job.fileCount ?? 0,
    },
    ...commonColumns.slice(1),
  ];
  const payableColumns = [
    commonColumns[0],
    {
      key: 'payableCount',
      header: 'Payables',
      style: widthStyle('payableCount'),
      render: (job) => job.payableCount ?? 0,
    },
    ...commonColumns.slice(1),
  ];
  const failedColumns = [
    commonColumns[0],
    {
      key: 'failureReason',
      header: 'Error',
      style: widthStyle('failureReason', {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        color: 'var(--red)',
        fontSize: '0.85rem',
      }),
      render: (job) => truncateError(job.failureReason),
    },
    ...commonColumns.slice(1),
  ];
  const processingColumns = [
    commonColumns[0],
    {
      key: 'status',
      header: 'Status',
      style: widthStyle('status'),
      render: (job) => (
        <span style={statusBadgeStyle(job.status)}>
          {processingStatusLabel(job.status)}
        </span>
      ),
    },
    ...commonColumns.slice(1),
  ];

  const sections = [
    {
      key: 'pendingVendor',
      label: 'Pending Vendor',
      empty: 'No invoices awaiting vendor identification.',
      section: pendingVendor,
      columns: vendorColumns,
      showDelete: true,
    },
    {
      key: 'pendingReview',
      label: 'Pending Review',
      empty: 'No invoices awaiting review.',
      section: pendingReview,
      columns: payableColumns,
      showDelete: true,
    },
    {
      key: 'failed',
      label: 'Failed',
      empty: 'No failed invoices.',
      section: failed,
      columns: failedColumns,
      showDelete: true,
    },
    {
      key: 'processing',
      label: 'Processing',
      empty: 'No invoices processing.',
      section: processing,
      columns: processingColumns,
      showDelete: true,
    },
    {
      key: 'archived',
      label: 'Archived',
      empty: 'No archived invoices.',
      section: archived,
      columns: payableColumns,
      showDelete: false,
    },
  ];

  const renderPagination = (section) => {
    if (section.total === 0) {
      return null;
    }
    const disabledPrev = section.page <= 1;
    const disabledNext = section.page >= section.pageCount;
    return (
      <div style={paginationStyle}>
        <button
          type="button"
          style={{
            ...paginationBtnStyle,
            opacity: disabledPrev ? 0.5 : 1,
            cursor: disabledPrev ? 'not-allowed' : 'pointer',
          }}
          disabled={disabledPrev}
          onClick={() => section.setPage(section.page - 1)}
          aria-label="Previous page"
        >
          ‹
        </button>
        <span style={{ fontSize: '0.875rem', color: 'var(--darkGrey)' }}>
          {section.page} / {section.pageCount}
        </span>
        <button
          type="button"
          style={{
            ...paginationBtnStyle,
            opacity: disabledNext ? 0.5 : 1,
            cursor: disabledNext ? 'not-allowed' : 'pointer',
          }}
          disabled={disabledNext}
          onClick={() => section.setPage(section.page + 1)}
          aria-label="Next page"
        >
          ›
        </button>
      </div>
    );
  };

  return (
    <div className="container scrollable" style={pageStyle}>
      <div>
        <p className="title">Vendor Invoices</p>
        <p className="sub-title">
          Upload invoice files and review pending payables
        </p>
      </div>

      {error && (
        <p style={{ color: 'var(--red)' }}>
          {error.message || 'Failed to load vendor invoice jobs.'}
        </p>
      )}

      <VendorInvoiceUploadPanel
        pendingFiles={pendingFiles}
        addFiles={addFiles}
        removeFile={removeFile}
        primaryLabel="Start processing"
        primaryLoadingLabel="Submitting..."
        primaryBusy={batchMutation.isPending}
        primaryDisabled={!canSubmit}
        onPrimary={handleSubmitBatch}
      />

      <div
        style={summaryRowStyle}
        role="navigation"
        aria-label="Invoice status counts"
      >
        {sections.map(({ key, label, section }) => (
          <button
            key={key}
            type="button"
            style={summaryBoxStyle}
            onClick={() => focusSection(key)}
          >
            <span
              style={{ fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.1 }}
            >
              {section.total}
            </span>
            <span style={{ fontSize: '0.8rem', color: 'var(--darkGrey)' }}>
              {label}
            </span>
          </button>
        ))}
      </div>

      {sections.map(({ key, label, empty, section, columns, showDelete }) => {
        const isCollapsed = collapsed[key];
        return (
          <section
            key={key}
            ref={(el) => {
              sectionRefs.current[key] = el;
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}
          >
            <button
              type="button"
              onClick={() => toggleSection(key)}
              aria-expanded={!isCollapsed}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                border: 'none',
                background: 'transparent',
                padding: 0,
                cursor: 'pointer',
                font: 'inherit',
                color: 'var(--black)',
                textAlign: 'left',
              }}
            >
              <span aria-hidden="true" style={{ width: '1rem' }}>
                {isCollapsed ? '▶' : '▼'}
              </span>
              <span style={{ fontSize: '1rem', fontWeight: 600 }}>
                {label} ({section.total})
              </span>
            </button>

            {!isCollapsed && (
              <>
                {!section.loading && section.jobs.length === 0 && (
                  <p style={emptyStyle}>{empty}</p>
                )}
                {section.jobs.length > 0 && (
                  <VendorInvoiceJobTable
                    jobs={section.jobs}
                    columns={columns}
                    onRowClick={navigateToJob}
                    onDeleted={invalidateLists}
                    showDelete={showDelete}
                  />
                )}
                {renderPagination(section)}
              </>
            )}
          </section>
        );
      })}
    </div>
  );
};

export default VendorInvoicesList;
